import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { GroupManagementService } from '@app/api/user-service/api/groupManagement.service';
import {
  KeycloakGroupRequest,
  KeycloakRoleRequest,
} from '@app/api/user-service';
import { MessageService } from 'primeng/api';
import { RolesService } from '../../services/roles.service';

@Component({
  selector: 'app-add-role-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    InputTextModule,
    AppButtonComponent,
  ],
  templateUrl: './add-role-dialog.component.html',
  styleUrls: ['./add-role-dialog.component.css'],
})
export class AddRoleDialogComponent {
  private readonly translate = inject(TranslateService);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly groupService = inject(GroupManagementService);
  private readonly rolesService = inject(RolesService);

  private readonly INVALID_CHARS_REGEX = /[!@#$%^&*/\\|<>?;:'",.`~+=[\]{}()]/;

  loading = signal(false);

  name = '';
  description = '';

  get messageService(): MessageService | undefined {
    return this.config?.data?.messageService as MessageService | undefined;
  }

  get parentId(): string | undefined {
    return this.config?.data?.parentId as string | undefined;
  }

  get mode(): 'group' | 'role' {
    return (this.config?.data?.mode as 'group' | 'role' | undefined) || 'group';
  }

  get hasInvalidChars(): boolean {
    return this.INVALID_CHARS_REGEX.test(this.name);
  }

  get isValid(): boolean {
    return this.name.trim().length > 0 && !this.hasInvalidChars;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.isValid) return;
    const name = this.name.trim();

    this.loading.set(true);
    if (this.mode === 'role') {
      const payload: KeycloakRoleRequest = {
        name,
        description: this.description,
        composite: false,
        clientRole: false,
      };

      this.rolesService.createRole(payload).subscribe({
        next: () => {
          this.loading.set(false);
          this.dialogRef.close({
            created: true,
            roleName: name,
            toast: {
              severity: 'success',
              summaryKey: 'common.messages.success',
              detailKey: 'configuration.roles.messages.create_role_success',
            },
          });
        },
        error: () => {
          this.loading.set(false);
          this.showCreateError();
        },
      });
      return;
    }

    const payload: KeycloakGroupRequest = {
      name,
      parentId: this.parentId,
      description: this.description,
    };

    this.groupService
      .createGroup(payload, 'body', false, { httpHeaderAccept: '*/*' as any })
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.dialogRef.close({ created: true, roleName: name });
        },
        error: () => {
          this.loading.set(false);
          this.showCreateError();
        },
      });
  }

  private showCreateError(): void {
    this.messageService?.add({
      severity: 'error',
      summary: this.translate.instant('common.messages.error'),
      detail: this.translate.instant(
        'configuration.roles.messages.create_role_failed',
      ),
    });
  }
}
